// Lesson instruction card — a DOM overlay (rem-sized, i18n-friendly) that shows
// the current step's instruction, a "Step n of total" progress line, inline
// wrong-move feedback, and the Next / Skip / Exit controls. Purely
// presentational: it renders a `LessonCardView` snapshot the lesson host derives
// from a `LessonController` and reports button taps back through callbacks. The
// gating + step logic all live in the controller (see lessons/LessonController).

import { clear, el } from '@bn-games/ui-shell';
import { t } from '../i18n/I18n.js';

// i18next v26's typed `t` rejects `string` keys; instruction + rejection keys
// are chosen at runtime from lesson data, so translate them through a narrowed
// alias (the CoachPanel does the same). Static keys keep the typed `t`.
const translate = t as unknown as (
  key: string,
  params?: Readonly<Record<string, unknown>>,
) => string;

export interface LessonCardCallbacks {
  readonly onNext: () => void;
  readonly onSkip: () => void;
  readonly onExit: () => void;
}

/** A flattened snapshot of the controller's current step, ready to render. */
export interface LessonCardView {
  readonly instructionKey: string;
  readonly instructionParams?: Readonly<Record<string, string | number>>;
  readonly stepIndex: number;
  readonly total: number;
  readonly canAdvance: boolean;
  readonly isComplete: boolean;
  readonly rejectionKey: string | null;
}

const CONTAINER_STYLE = [
  'position:fixed',
  'left:50%',
  'top:1rem',
  'transform:translateX(-50%)',
  'box-sizing:border-box',
  'width:min(32rem,calc(100% - 2rem))',
  'display:flex',
  'flex-direction:column',
  'gap:0.6rem',
  'padding:1rem 1.25rem',
  'border-radius:0.75rem',
  'background:rgba(6,32,24,0.9)',
  'box-shadow:0 0.25rem 0.75rem rgba(0,0,0,0.4)',
  'color:#f4e6c0',
  'z-index:25',
  'font-family:system-ui,-apple-system,sans-serif',
].join(';');

const PROGRESS_STYLE = 'margin:0;font-size:0.8rem;letter-spacing:0.04em;opacity:0.7';
const INSTRUCTION_STYLE = 'margin:0;font-size:1.05rem;line-height:1.4';
const FEEDBACK_STYLE = 'margin:0;font-size:0.9rem;line-height:1.35;color:#ffb4a2';
const ROW_STYLE = 'display:flex;gap:0.6rem;justify-content:flex-end;flex-wrap:wrap';

function buttonStyle(background: string, color: string): string {
  return [
    'min-width:5rem',
    'padding:0.55rem 1rem',
    'border:none',
    'border-radius:0.5rem',
    'font-size:0.95rem',
    'font-weight:600',
    'cursor:pointer',
    `background:${background}`,
    `color:${color}`,
  ].join(';');
}

const PRIMARY_BG = '#f0d898';
const PRIMARY_INK = '#1a1614';
const GHOST_BG = 'rgba(255,255,255,0.14)';
const GHOST_INK = '#f4e6c0';

export class LessonCard {
  private readonly root: HTMLElement;

  constructor(private readonly callbacks: LessonCardCallbacks) {
    this.root = el('div', { attrs: { style: `${CONTAINER_STYLE};display:none` } });
    document.body.appendChild(this.root);
  }

  update(view: LessonCardView): void {
    clear(this.root);
    this.root.appendChild(
      el('p', {
        text: t('lesson.progress', { n: view.stepIndex + 1, total: view.total }),
        attrs: { style: PROGRESS_STYLE },
      }),
    );
    this.root.appendChild(
      el('p', {
        text: view.isComplete
          ? t('lesson.complete')
          : translate(view.instructionKey, view.instructionParams),
        attrs: { style: INSTRUCTION_STYLE },
      }),
    );
    if (view.rejectionKey !== null) {
      this.root.appendChild(
        el('p', { text: translate(view.rejectionKey), attrs: { style: FEEDBACK_STYLE } }),
      );
    }
    this.root.appendChild(this.controls(view));
    this.root.style.display = 'flex';
  }

  clear(): void {
    clear(this.root);
    this.root.style.display = 'none';
  }

  dispose(): void {
    this.root.remove();
  }

  private controls(view: LessonCardView): HTMLElement {
    const buttons: HTMLElement[] = [
      this.button(t('lesson.exit'), GHOST_BG, GHOST_INK, this.callbacks.onExit),
    ];
    if (!view.isComplete) {
      buttons.push(this.button(t('lesson.skip'), GHOST_BG, GHOST_INK, this.callbacks.onSkip));
    }
    if (view.canAdvance || view.isComplete) {
      buttons.push(this.button(t('lesson.next'), PRIMARY_BG, PRIMARY_INK, this.callbacks.onNext));
    }
    return el('div', { attrs: { style: ROW_STYLE } }, buttons);
  }

  private button(label: string, background: string, color: string, onClick: () => void): HTMLElement {
    return el('button', {
      text: label,
      attrs: { type: 'button', style: buttonStyle(background, color) },
      on: { click: onClick },
    });
  }
}
